import { useMemo, useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import type { PurchasingNewProductInput, PurchasingProductRow } from '@/services/purchasingTypes'

type PurchasingCreateProductDialogProps = {
  open: boolean
  initialName: string
  defaultAtomicUom: string
  products: PurchasingProductRow[]
  template?: PurchasingNewProductInput | null
  onClose: () => void
  onSubmit: (payload: PurchasingNewProductInput) => Promise<void>
}

export default function PurchasingCreateProductDialog({
  open,
  initialName,
  defaultAtomicUom,
  products,
  template,
  onClose,
  onSubmit,
}: PurchasingCreateProductDialogProps) {
  const cogsOptions = useMemo(() => {
    const ids = [...new Set(products.map((p) => p.cogs_account_id).filter(Boolean))]
    return ids.sort()
  }, [products])

  const [name, setName] = useState(initialName)
  const [cogsAccountId, setCogsAccountId] = useState(
    template?.cogs_account_id ?? cogsOptions[0] ?? '',
  )
  const [cogsCustom, setCogsCustom] = useState('')
  const [atomicUom, setAtomicUom] = useState(template?.atomic_uom ?? defaultAtomicUom)
  const [busy, setBusy] = useState(false)

  if (!open) return null

  const resolvedCogs = cogsCustom.trim() || cogsAccountId

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog">
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
        <h2 className="text-base font-semibold text-gray-900">Create product</h2>
        <div className="mt-3 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-gray-700">Name</span>
            <input className="input w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-gray-700">COGS account</span>
            {cogsOptions.length > 0 ? (
              <select
                className="input w-full"
                value={cogsAccountId}
                onChange={(e) => setCogsAccountId(e.target.value)}
              >
                {cogsOptions.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            ) : null}
            <input
              className="input mt-1 w-full"
              placeholder="Account id"
              value={cogsCustom}
              onChange={(e) => setCogsCustom(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-gray-700">Atomic UOM</span>
            <input className="input w-full" value={atomicUom} onChange={(e) => setAtomicUom(e.target.value)} />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy || !name.trim() || !resolvedCogs || !atomicUom.trim()}
            onClick={() => {
              setBusy(true)
              void onSubmit({
                name: name.trim(),
                cogs_account_id: resolvedCogs,
                atomic_uom: atomicUom.trim(),
                family: template?.family,
                brand_policy: template?.brand_policy,
              }).finally(() => setBusy(false))
            }}
          >
            Create & match
          </Button>
        </div>
      </div>
    </div>
  )
}
